import {
  errorToastClassName,
  successToastClassName,
  appToast as toast,
  warningToastClassName,
} from "@/components/ui/baseUi/sonner"
import { useBoxProgramSlotDefaultSettings } from "@/modules/boxProgrammation/hooks/useBoxProgramSlotDefaultSettings"
import { getValidationSchema } from "@modules/boxSlotTemplate/boxSlotTemplateGroupFormikTypes"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useFormik } from "formik"
import { useEffect, useState } from "react"
import { useBoxSlotTemplateActions } from "@/modules/boxSlotTemplate/store/useBoxSlotTemplateStore"
import { useBoxSlotTemplateCreateList } from "@/modules/boxSlotTemplate/hooks/useBoxSlotTemplates"
import { BoxSlotTemplateGroupFormikEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateGroupFormikEntity"
import { BoxSlotTemplateEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateEntity"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"

export const useBoxSlotTemplateGroupFormik = (boxProgramId: string) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { data: boxProgram } = useBoxProgram(boxProgramId)
  const { data: defaultSettings } = useBoxProgramSlotDefaultSettings(boxProgramId)
  const { hideSidebar } = useSidebar()
  const { addBoxSlotTemplateBeingCreated, clearBoxSlotTemplateBeingCreated } = useBoxSlotTemplateActions()
  const { mutateAsync: createBoxSlotsTemplateList } = useBoxSlotTemplateCreateList()

  const formik = useFormik<BoxSlotTemplateGroupFormikEntity>({
    initialValues: BoxSlotTemplateGroupFormikEntity.fromDefaultSettings(defaultSettings, boxProgramId),
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    onSubmit: async () => {
      if (!isSubmitting) {
        setIsSubmitting(true)
        try {
          await createBoxSlotsTemplateList({
            boxProgramId,
            boxSlotTemplates: formik.values.toInputDto(),
          })

          toast.success(word("box.slotTemplateGroup.form.toast.success"), {
            classNames: successToastClassName(),
          })
          hideSidebar(false)
        } catch (e: unknown) {
          if (typeof e === "object" && e !== null && "message" in e && typeof e.message === "string") {
            toast.error(word("global.error.label"), {
              description: e.message,
              classNames: errorToastClassName(),
            })
          }
        } finally {
          setIsSubmitting(false)
        }
      }
    },
  })

  useEffect(() => {
    const boxSlotTemplatesBeingCreated = BoxSlotTemplateEntity.fromGroupFormikEntity(formik.values, boxProgram)

    if (boxSlotTemplatesBeingCreated.length < 100) {
      // add the created slots (overlayed) to the calendar in background
      addBoxSlotTemplateBeingCreated(boxSlotTemplatesBeingCreated)
    } else {
      toast.warning(word("box.slotTemplateGroup.form.toast.tooManyToDisplay"), {
        classNames: warningToastClassName(),
      })
    }
    return () => {
      // clear the overlayed slots in calendar
      clearBoxSlotTemplateBeingCreated()
    }
  }, [
    formik.values.slotTemplatesRanges,
    formik.values.slotDuration,
    formik.values.assignedStaffMembers,
    boxProgram,
    addBoxSlotTemplateBeingCreated,
    clearBoxSlotTemplateBeingCreated,
  ])

  return formik
}
